import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { instituteService } from '../../../services/instituteService';
import { boothEnhancementsService } from '../../../services/boothEnhancementsService';
import { notificationService } from '../../../services/notificationService';
import { leadService } from '../../../services/leadService';
import { useToast } from '../../../contexts/ToastContext';


const InteractionPanel = ({ isAvailable, nextAvailableTime, instituteId, instituteName, instituteEmail, institutePhone, chatEnabled = false, videoEnabled = false, tour360Enabled = false, whatsappEnabled = false }) => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const toast = useToast();
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showCallbackModal, setShowCallbackModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);
  const [reportIssue, setReportIssue] = useState({ type: 'technical', description: '' });
  const [callbackRequest, setCallbackRequest] = useState({ name: '', phone: '', preferredTime: '' });
  const [isSaved, setIsSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contactType, setContactType] = useState('phone');
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applicationLinks] = useState([]);

  // Need Help inline form state
  const [helpForm, setHelpForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [helpFormLoading, setHelpFormLoading] = useState(false);
  const [helpFormSuccess, setHelpFormSuccess] = useState(false);

  const timeSlots = [
    '09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM'
  ];

  const issueTypes = [
    { value: 'technical', label: 'Technical Issue' },
    { value: 'content', label: 'Content Problem' },
    { value: 'inappropriate', label: 'Inappropriate Content' },
    { value: 'other', label: 'Other' }
  ];

  const parseTimeSlotTo24h = (slot) => {
    const [time, period] = slot?.split(' ') || [];
    let [hours, minutes] = (time || '00:00')?.split(':')?.map(Number);
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return `${String(hours)?.padStart(2, '0')}:${String(minutes)?.padStart(2, '0')}`;
  };

  const handleSchedule = async () => {
    if (!selectedDate || !selectedTime) return;

    if (!user) {
      toast?.warning('Please login to book a session');
      navigate('/student-registration');
      return;
    }

    try {
      setBookingLoading(true);
      const bookingTime = parseTimeSlotTo24h(selectedTime);
      await boothEnhancementsService?.createBooking({
        instituteId,
        studentId: user?.id,
        bookingDate: selectedDate,
        bookingTime,
        durationMinutes: 30,
        notes: ''
      });

      leadService?.captureLeadSilently(user?.id, instituteId, 'video_session', null, true);

      setShowScheduleModal(false);
      setSelectedDate('');
      setSelectedTime('');
      toast?.success('Session confirmed! Check your dashboard for details.');
    } catch (error) {
      console.error('Error booking session:', error);
      if (error?.message?.includes('unique') || error?.code === '23505') {
        toast?.error('This slot was just booked by someone else. Please select another time.');
      } else {
        toast?.error('Failed to book session. Please try again.');
      }
    } finally {
      setBookingLoading(false);
    }
  };

  const handleSaveInstitute = async () => {
    if (!user) {
      alert('Please login to save institutes');
      navigate('/student-registration');
      return;
    }

    if (!instituteId) {
      alert('Institute information not available');
      return;
    }

    try {
      setLoading(true);
      if (isSaved) {
        await instituteService?.unsaveInstitute(user?.id, instituteId);
        setIsSaved(false);
        alert('Institute removed from saved list');
      } else {
        await instituteService?.saveInstitute(user?.id, instituteId);
        setIsSaved(true);
        alert('Institute saved successfully!');
      }
    } catch (error) {
      console.error('Error saving institute:', error);
      alert('Failed to save institute. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleShareBooth = async () => {
    const shareData = {
      title: instituteName || 'Virtual Booth',
      text: `Check out ${instituteName || 'this institute'} at the Virtual Education Fair!`,
      url: window.location?.href
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: Copy to clipboard
        await navigator.clipboard?.writeText(window.location?.href);
        alert('Booth link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      // Fallback for any errors
      try {
        await navigator.clipboard?.writeText(window.location?.href);
        alert('Booth link copied to clipboard!');
      } catch (clipboardError) {
        alert('Unable to share. Please copy the URL manually.');
      }
    }
  };

  const handleReportIssue = async () => {
    if (!reportIssue?.description?.trim()) {
      alert('Please describe the issue');
      return;
    }

    try {
      setLoading(true);
      // Send notification to admin about the report
      await notificationService?.sendEmail({
        to: 'admin@studyexpo.com',
        subject: `Issue Report: ${reportIssue?.type}`,
        html: `
          <h2>Issue Report</h2>
          <p><strong>Type:</strong> ${reportIssue?.type}</p>
          <p><strong>Institute:</strong> ${instituteName || 'Unknown'}</p>
          <p><strong>Reported by:</strong> ${user?.email || 'Anonymous'}</p>
          <p><strong>Description:</strong></p>
          <p>${reportIssue?.description}</p>
          <p><strong>Page URL:</strong> ${window.location?.href}</p>
        `,
        type: 'report'
      });
      
      alert('Thank you for reporting. We will investigate this issue.');
      setShowReportModal(false);
      setReportIssue({ type: 'technical', description: '' });
    } catch (error) {
      console.error('Error reporting issue:', error);
      alert('Failed to submit report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCallback = async () => {
    if (!callbackRequest?.name?.trim() || !callbackRequest?.phone?.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      // Send notification to institute about callback request
      await notificationService?.sendEmail({
        to: instituteEmail || 'institute@example.com',
        subject: `Callback Request from ${callbackRequest?.name}`,
        html: `
          <h2>Callback Request</h2>
          <p><strong>Name:</strong> ${callbackRequest?.name}</p>
          <p><strong>Phone:</strong> ${callbackRequest?.phone}</p>
          <p><strong>Preferred Time:</strong> ${callbackRequest?.preferredTime || 'Anytime'}</p>
          <p><strong>Student Email:</strong> ${user?.email || 'Not provided'}</p>
        `,
        type: 'callback'
      });
      
      alert('Callback request submitted! The institute will contact you soon.');
      setShowCallbackModal(false);
      setCallbackRequest({ name: '', phone: '', preferredTime: '' });
    } catch (error) {
      console.error('Error requesting callback:', error);
      alert('Failed to submit callback request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartChat = () => {
    if (!user) {
      toast?.warning('Please register or login to start a chat');
      navigate('/student-registration');
      return;
    }

    if (!chatEnabled) {
      alert('Chat access not activated. Please contact admin.');
      return;
    }

    // Check institute approval status
    const isInstitute = userProfile?.role === 'institute';
    const instituteProfile = userProfile?.institute_profiles?.[0];
    if (isInstitute && instituteProfile?.approval_status !== 'approved') {
      alert('Your account is pending admin approval. Chat and video counseling will be available once approved.');
      return;
    }

    // Capture lead on chat initiation
    if (user?.id && instituteId) {
      leadService?.captureLeadSilently(user?.id, instituteId, 'chat', null, true);
    }

    navigate('/chat-interface');
  };

  const handleWhatsAppClick = () => {
    if (!user) {
      toast?.warning('Please register or login to contact via WhatsApp');
      navigate('/student-registration');
      return;
    }
    const phone = institutePhone?.replace(/\D/g, '');
    const message = encodeURIComponent(`Hi, I'm interested in learning more about ${instituteName || 'your institute'}. I found your booth on StudyExpo.`);
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  const handleEmailClick = () => {
    if (!user) {
      toast?.warning('Please register or login to email the institute');
      navigate('/student-registration');
      return;
    }
    const subject = encodeURIComponent(`Enquiry about ${instituteName || 'your institute'} - StudyExpo`);
    const body = encodeURIComponent(`Hello,\n\nI came across your institute on StudyExpo and would like to know more about your programs and admission process.\n\nPlease share the relevant details.\n\nThank you.`);
    window.open(`mailto:${instituteEmail}?subject=${subject}&body=${body}`, '_blank');
  };

  const handleCallbackClick = () => {
    if (!user) {
      toast?.warning('Please register or login to request a callback');
      navigate('/student-registration');
      return;
    }
    setShowCallbackModal(true);
  };

  const handleContactClick = (type) => {
    setContactType(type);
    setShowContactModal(true);
  };

  const handleApplyNow = () => {
    if (!user) {
      alert('Please login to apply');
      navigate('/student-registration');
      return;
    }

    // Capture lead on Apply Now click
    if (user?.id && instituteId) {
      leadService?.captureLeadSilently(user?.id, instituteId, 'apply_now', null, true);
    }

    if (applicationLinks?.length === 0) {
      alert('Application link not available at the moment. Please contact the institute directly via chat or phone.');
      return;
    }

    if (applicationLinks?.length === 1) {
      // Single link - redirect directly
      window.open(applicationLinks?.[0]?.url, '_blank');
    } else {
      // Multiple links - show modal
      setShowApplyModal(true);
    }
  };

  const handleStart360Tour = () => {
    if (!tour360Enabled) {
      alert('360° Tour not activated. Please contact admin.');
      return;
    }
    navigate(`/360-campus-tour/${instituteId}`);
  };

  return (
    <>
      <div className="bg-card rounded-lg shadow-md p-4 md:p-5 lg:p-6">
        <div className="flex items-center gap-2 mb-4 md:mb-5">
          <Icon name="MessageSquare" size={20} color="var(--color-primary)" />
          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">
            Connect with Us
          </h3>
        </div>

        <div className="space-y-3 md:space-y-4">
          {/* Online/Offline Status Indicator */}
          <div className={`p-4 rounded-lg border ${isAvailable ? 'bg-success/5 border-success/20' : 'bg-muted border-border'}`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="relative flex h-3 w-3">
                {isAvailable ? (
                  <>
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75" />
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-success" />
                  </>
                ) : (
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-muted-foreground/50" />
                )}
              </span>
              <span className={`text-sm font-semibold ${isAvailable ? 'text-success' : 'text-muted-foreground'}`}>
                {isAvailable ? 'Counsellor is Online' : 'Counsellor is Offline'}
              </span>
            </div>
            <p className="text-xs text-muted-foreground ml-5">
              {isAvailable
                ? 'Available now — start a live chat or schedule a session' :'Not available right now — leave a message or reach out on WhatsApp'}
            </p>
          </div>

          <Button
            variant="default"
            fullWidth
            iconName="MessageSquare"
            iconPosition="left"
            disabled={!isAvailable || !chatEnabled}
            onClick={handleStartChat}
          >
            {!chatEnabled ? 'Chat Not Activated' : isAvailable ? 'Start Live Chat' : 'Chat Unavailable'}
          </Button>

          {/* WhatsApp Button — only shown when super admin has enabled it */}
          {whatsappEnabled && institutePhone && institutePhone !== 'N/A' && (
            <div>
              {!isAvailable && (
                <div className="flex items-center gap-1.5 mb-1.5 px-1">
                  <Icon name="AlertCircle" size={13} color="#16a34a" />
                  <span className="text-xs font-medium text-green-700">
                    Counsellor is offline — Chat on WhatsApp instead
                  </span>
                </div>
              )}
              <Button
                variant={!isAvailable ? 'default' : 'outline'}
                fullWidth
                iconName="MessageCircle"
                iconPosition="left"
                onClick={handleWhatsAppClick}
                className={!isAvailable
                  ? 'bg-green-600 hover:bg-green-700 text-white border-green-600 shadow-md'
                  : 'border-green-500 text-green-600 hover:bg-green-50'}
              >
                Chat on WhatsApp
              </Button>
            </div>
          )}

          {/* Email Button */}
          {instituteEmail && instituteEmail !== 'N/A' && (
            <Button
              variant="outline"
              fullWidth
              iconName="Mail"
              iconPosition="left"
              onClick={handleEmailClick}
              className="border-blue-500 text-blue-600 hover:bg-blue-50"
            >
              Email Us
            </Button>
          )}

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-card px-2 text-muted-foreground">or</span>
            </div>
          </div>

          <Button
            variant="outline"
            fullWidth
            iconName="Video"
            iconPosition="left"
            disabled={!videoEnabled}
            onClick={() => {
              if (!videoEnabled) {
                alert('Video counseling not activated. Please contact admin.');
                return;
              }
              const isInstitute = userProfile?.role === 'institute';
              const instituteProfile = userProfile?.institute_profiles?.[0];
              if (isInstitute && instituteProfile?.approval_status !== 'approved') {
                alert('Your account is pending admin approval. Chat and video counseling will be available once approved.');
                return;
              }
              setShowScheduleModal(true);
            }}
          >
            {!videoEnabled ? 'Video Counseling Not Activated' : 'Schedule Video Counseling'}
          </Button>

          <Button
            variant="outline"
            fullWidth
            iconName="Camera"
            iconPosition="left"
            disabled={!tour360Enabled}
            onClick={handleStart360Tour}
            title={!tour360Enabled ? '360° Tour not activated. Please contact admin.' : 'Take a 360° Campus Tour'}
          >
            {!tour360Enabled ? '360° Tour Not Activated' : '360° Campus Tour'}
          </Button>

          <Button
            variant="outline"
            fullWidth
            iconName="PhoneCall"
            iconPosition="left"
            onClick={handleCallbackClick}
          >
            Request a Callback
          </Button>

          <div className="grid grid-cols-2 gap-2 md:gap-3">
            <Button
              variant="ghost"
              size="sm"
              iconName={isSaved ? 'Heart' : 'Heart'}
              iconPosition="left"
              onClick={handleSaveInstitute}
              disabled={loading}
              className={isSaved ? 'text-accent' : ''}
            >
              {isSaved ? 'Saved' : 'Save'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconName="Share2"
              iconPosition="left"
              onClick={handleShareBooth}
            >
              Share
            </Button>
          </div>
        </div>
      </div>

      {/* Need Help Section - Moved below Connect with Us */}
      <div className="bg-card rounded-lg shadow-md p-4 md:p-5 lg:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="HelpCircle" size={20} color="var(--color-primary)" />
          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">
            Need Help?
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Our admissions team is here to assist you with any questions about programs, eligibility, or the application process.
        </p>

        {helpFormSuccess ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
              <Icon name="CheckCircle" size={24} color="var(--color-success)" />
            </div>
            <p className="text-sm font-medium text-foreground">Message sent!</p>
            <p className="text-xs text-muted-foreground">The counsellor will get back to you soon.</p>
            <button
              onClick={() => { setHelpFormSuccess(false); setHelpForm({ name: '', phone: '', email: '', message: '' }); }}
              className="text-xs text-primary underline mt-1"
            >
              Send another message
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Your Name *</label>
              <input
                type="text"
                value={helpForm?.name}
                onChange={(e) => setHelpForm({ ...helpForm, name: e?.target?.value })}
                placeholder="Enter your name"
                className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground placeholder:text-muted-foreground"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Phone Number *</label>
              <input
                type="tel"
                value={helpForm?.phone}
                onChange={(e) => setHelpForm({ ...helpForm, phone: e?.target?.value })}
                placeholder="Enter your phone number"
                className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground placeholder:text-muted-foreground"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Email</label>
              <input
                type="email"
                value={helpForm?.email}
                onChange={(e) => setHelpForm({ ...helpForm, email: e?.target?.value })}
                placeholder="Enter your email (optional)"
                className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground placeholder:text-muted-foreground"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Your Message *</label>
              <textarea
                value={helpForm?.message}
                onChange={(e) => setHelpForm({ ...helpForm, message: e?.target?.value })}
                rows={3}
                placeholder="Describe your query or what you need help with..."
                className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground placeholder:text-muted-foreground resize-none"
              />
            </div>
            <div className="space-y-2">
              <Button
                variant="default"
                fullWidth
                size="sm"
                iconName={helpFormLoading ? 'Loader' : 'Send'}
                iconPosition="left"
                disabled={helpFormLoading}
                onClick={async () => {
                  if (!helpForm?.name?.trim() || !helpForm?.phone?.trim() || !helpForm?.message?.trim()) {
                    toast?.warning('Please fill in your name, phone, and message.');
                    return;
                  }
                  try {
                    setHelpFormLoading(true);
                    await notificationService?.sendEmail({
                      to: instituteEmail || 'institute@example.com',
                      subject: `Help Request from ${helpForm?.name} — ${instituteName || 'Virtual Booth'}`,
                      html: `
                        <h2>New Help Request from StudyExpo Virtual Booth</h2>
                        <p><strong>Institute:</strong> ${instituteName || 'N/A'}</p>
                        <hr/>
                        <p><strong>Student Name:</strong> ${helpForm?.name}</p>
                        <p><strong>Phone:</strong> ${helpForm?.phone}</p>
                        <p><strong>Email:</strong> ${helpForm?.email || 'Not provided'}</p>
                        <p><strong>Message:</strong></p>
                        <blockquote style="border-left:4px solid #ccc;padding-left:12px;color:#555;">${helpForm?.message}</blockquote>
                        <hr/>
                        <p style="font-size:12px;color:#888;">Sent via StudyExpo Virtual Booth — ${window.location?.href}</p>
                      `,
                      type: 'help_request'
                    });
                    if (user?.id && instituteId) {
                      leadService?.captureLeadSilently(user?.id, instituteId, 'help_request', null, true);
                    }
                    setHelpFormSuccess(true);
                  } catch (error) {
                    console.error('Error sending help request:', error);
                    toast?.error('Failed to send message. Please try again.');
                  } finally {
                    setHelpFormLoading(false);
                  }
                }}
              >
                {helpFormLoading ? 'Sending...' : 'Send Message to Counsellor'}
              </Button>
              <Button
                variant="outline"
                fullWidth
                size="sm"
                iconName="MessageSquare"
                iconPosition="left"
                onClick={handleStartChat}
              >
                Start Chat
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Schedule Modal */}
      {showScheduleModal && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-1030"
            onClick={() => setShowScheduleModal(false)}
          />
          <div className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-md bg-popover rounded-lg shadow-2xl z-1030 overflow-hidden">
            <div className="flex items-center justify-between p-4 md:p-5 border-b border-border">
              <h3 className="text-lg font-heading font-semibold text-foreground">
                Schedule Counseling
              </h3>
              <button
                onClick={() => setShowScheduleModal(false)}
                className="p-2 hover:bg-muted rounded-lg transition-smooth"
                aria-label="Close modal"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-4 md:p-5 max-h-[calc(100vh-200px)] md:max-h-96 overflow-y-auto">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Select Date
                  </label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e?.target?.value)}
                    min={new Date()?.toISOString()?.split('T')?.[0]}
                    className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Select Time Slot
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {timeSlots?.map((slot) => (
                      <button
                        key={slot}
                        onClick={() => setSelectedTime(slot)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-smooth ${
                          selectedTime === slot
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-foreground hover:bg-muted/80'
                        }`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">
                    <Icon name="Info" size={14} className="inline mr-1" />
                    You will receive a confirmation email with the meeting link before the scheduled time.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 p-4 md:p-5 border-t border-border">
              <Button
                variant="outline"
                fullWidth
                onClick={() => setShowScheduleModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                fullWidth
                onClick={handleSchedule}
                disabled={!selectedDate || !selectedTime || bookingLoading}
              >
                {bookingLoading ? 'Booking...' : 'Confirm Schedule'}
              </Button>
            </div>
          </div>
        </>
      )}
      {/* Report Issue Modal */}
      {showReportModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-1020" onClick={() => setShowReportModal(false)} />
          <div className="fixed inset-0 z-1030 flex items-center justify-center p-4">
            <div className="bg-card rounded-lg shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-4 md:p-5 border-b border-border">
                <h3 className="text-lg font-heading font-semibold text-foreground">Report Issue</h3>
                <button onClick={() => setShowReportModal(false)} className="p-1 hover:bg-muted rounded-lg transition-smooth">
                  <Icon name="X" size={20} color="var(--color-muted-foreground)" />
                </button>
              </div>
              <div className="p-4 md:p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Issue Type</label>
                  <select
                    value={reportIssue?.type}
                    onChange={(e) => setReportIssue({ ...reportIssue, type: e?.target?.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  >
                    {issueTypes?.map((type) => (
                      <option key={type?.value} value={type?.value}>{type?.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Description</label>
                  <textarea
                    value={reportIssue?.description}
                    onChange={(e) => setReportIssue({ ...reportIssue, description: e?.target?.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                    placeholder="Please describe the issue..."
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="default" onClick={handleReportIssue} disabled={loading}>Submit</Button>
                  <Button variant="outline" onClick={() => setShowReportModal(false)}>Cancel</Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {/* Callback Request Modal */}
      {showCallbackModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-1020" onClick={() => setShowCallbackModal(false)} />
          <div className="fixed inset-0 z-1030 flex items-center justify-center p-4">
            <div className="bg-card rounded-lg shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-4 md:p-5 border-b border-border">
                <h3 className="text-lg font-heading font-semibold text-foreground">Request Callback</h3>
                <button onClick={() => setShowCallbackModal(false)} className="p-1 hover:bg-muted rounded-lg transition-smooth">
                  <Icon name="X" size={20} color="var(--color-muted-foreground)" />
                </button>
              </div>
              <div className="p-4 md:p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Your Name *</label>
                  <input
                    type="text"
                    value={callbackRequest?.name}
                    onChange={(e) => setCallbackRequest({ ...callbackRequest, name: e?.target?.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    value={callbackRequest?.phone}
                    onChange={(e) => setCallbackRequest({ ...callbackRequest, phone: e?.target?.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Preferred Time</label>
                  <input
                    type="text"
                    value={callbackRequest?.preferredTime}
                    onChange={(e) => setCallbackRequest({ ...callbackRequest, preferredTime: e?.target?.value })}
                    placeholder="e.g., Morning, Afternoon, Evening"
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="default" onClick={handleRequestCallback} disabled={loading}>Submit</Button>
                  <Button variant="outline" onClick={() => setShowCallbackModal(false)}>Cancel</Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {/* Contact Information Modal */}
      {showContactModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-1020" onClick={() => setShowContactModal(false)} />
          <div className="fixed inset-0 z-1030 flex items-center justify-center p-4">
            <div className="bg-card rounded-lg shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-4 md:p-5 border-b border-border">
                <h3 className="text-lg font-heading font-semibold text-foreground">Contact Information</h3>
                <button onClick={() => setShowContactModal(false)} className="p-1 hover:bg-muted rounded-lg transition-smooth">
                  <Icon name="X" size={20} color="var(--color-muted-foreground)" />
                </button>
              </div>
              <div className="p-4 md:p-5 space-y-4">
                {contactType === 'phone' && (
                  <div className="flex items-center gap-3">
                    <Icon name="Phone" size={24} color="var(--color-primary)" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Phone</p>
                      <p className="text-base text-foreground data-text">{institutePhone || 'N/A'}</p>
                    </div>
                  </div>
                )}
                {contactType === 'email' && (
                  <div className="flex items-center gap-3">
                    <Icon name="Mail" size={24} color="var(--color-primary)" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Email</p>
                      <p className="text-base text-foreground">{instituteEmail || 'N/A'}</p>
                    </div>
                  </div>
                )}
                <Button variant="default" fullWidth onClick={() => setShowContactModal(false)}>Close</Button>
              </div>
            </div>
          </div>
        </>
      )}
      {/* Apply Now Modal - Multiple Links */}
      {showApplyModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-1020" onClick={() => setShowApplyModal(false)} />
          <div className="fixed inset-0 z-1030 flex items-center justify-center p-4">
            <div className="bg-card rounded-lg shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-4 md:p-5 border-b border-border">
                <h3 className="text-lg font-heading font-semibold text-foreground">Application Links</h3>
                <button onClick={() => setShowApplyModal(false)} className="p-1 hover:bg-muted rounded-lg transition-smooth">
                  <Icon name="X" size={20} color="var(--color-muted-foreground)" />
                </button>
              </div>
              <div className="p-4 md:p-5 space-y-3">
                {applicationLinks?.map((link, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    fullWidth
                    iconName="ExternalLink"
                    iconPosition="right"
                    onClick={() => window.open(link?.url, '_blank')}
                  >
                    {link?.name || `Application ${index + 1}`}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default InteractionPanel;